import { SpokePageSpinner } from '@doltech/core/lib/auth/SpokePageSpinner';
import { DynamicPageProps } from '@doltech/routeDefinition';
import { dolDinhLucAuthor } from '@doltech/ui/lib/figma/Blog/dolDinhLucAuthor.data';
import { Box } from '@doltech/ui/lib/figma/Common/BoxV2';
import { DownloadPDF } from '@doltech/ui/lib/figma/Layout/Landing/Content/DownloadPdf';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { AuthorInfo } from '@doltech/ui/lib/figma/Layout/Landing/Header/AuthorInfo';
import { useLandingSharedDataStore } from '@doltech/ui/lib/figma/Layout/Landing/model/landing-shared-data.hook';
import { bannerOnlineTest } from '@doltech/ui/lib/figma/Layout/Landing/PremadeLayout/bannerImages';
import { LandingLayoutWithSEOHeader } from '@doltech/ui/lib/figma/Layout/Landing/PremadeLayout/LandingLayoutWithSEOHeader';
import { withDolPremiumStore } from '@doltech/ui/lib/hooks/dolPremium/withDolPremium';
import { ExtraInfoViewProps } from '@doltech/ui/lib/shared/components/ExtraInfo/ExtraInfoView';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { PDFSourceContext } from '@doltech/utils/lib/pdf';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import * as React from 'react';
import withHydrationOnDemand from '@doltech/core/lib/hooks/withDolHydrationOnDemand';

import { useViewTestSubjectStore } from './model/view-test-subject.hook';
import { ViewSubjectHeaderWithTestAction } from './ViewSubjectHeader';
import { ViewTestSubjectPdfLayout } from './ViewTestSubjectPdfLayout';

const FooterBanner = withHydrationOnDemand({
  on: ['idle'],
})(
  dynamic(
    import('@doltech/ui/lib/banner/component/HeaderBanner').then((mod) => mod.FooterBanner),
    {
      ssr: false,
    }
  )
);

const SubjectListeningContent = dynamic<any>(
  () => import('./SubjectListeningContent').then((mod) => mod.SubjectListeningContent),
  {
    loading: () => <SpokePageSpinner />,
  }
);

const SubjectReadingContent = dynamic<any>(
  () => import('./SubjectReadingContent').then((mod) => mod.SubjectReadingContent as any),
  {
    loading: () => <SpokePageSpinner />,
  }
);

const ExtraInfoView = withHydrationOnDemand({
  on: ['idle'],
})(
  dynamic<ExtraInfoViewProps>(
    () =>
      import('@doltech/ui/lib/shared/components/ExtraInfo/ExtraInfoView').then(
        (mod) => mod.ExtraInfoView
      ),
    {
      loading: () => <div>Loading...</div>,
    }
  )
);

interface ViewTestSubjectLayoutProps extends DynamicPageProps {}

const mapTestTypeAndSkillToSourceTracking = (skill: SkillEnum, testType: TestTypeEnum) => {
  if (TestTypeEnum.FULL_TEST === testType) {
    if (SkillEnum.READING === skill) {
      return 'FULL_TEST_READING';
    }
    if (SkillEnum.LISTENING === skill) {
      return 'FULL_TEST_LISTENING';
    }
    return 'FULL_TEST';
  }
  if (SkillEnum.READING === skill) {
    return 'PRACTICE_TEST_READING';
  }
  if (SkillEnum.LISTENING === skill) {
    return 'PRACTICE_TEST_LISTENING';
  }
  return 'PRACTICE_TEST';
};

export const ViewTestSubjectLayout = withDolPremiumStore({})(
  observer((props: ViewTestSubjectLayoutProps) => {
    const { internalPageLink, data, seoData, pageData, ...rest } = props;
    const { extraInfo } = seoData;

    const store = useViewTestSubjectStore();
    const { getHomePageLink } = useLandingSharedDataStore();
    return (
      <LandingLayoutWithSEOHeader
        testType={data.testType}
        testSkill={store.skill}
        tableOfContentItems={store.getTableOfContent()}
        testId={data.id}
        data={data}
        seoData={seoData}
        pageData={pageData}
        socials={[]}
        banner={bannerOnlineTest}
        bannerLink={internalPageLink?.staticPageLink?.LANDING_FULL_TEST?.url || '/'}
        {...rest}
      >
        {store.skill === SkillEnum.LISTENING ? (
          <SubjectListeningContent />
        ) : (
          <SubjectReadingContent />
        )}

        <PDFSourceContext.Provider
          value={{ source: mapTestTypeAndSkillToSourceTracking(store.skill, store.testType) }}
        >
          <DownloadPDF
            id="test-section-download-pdf"
            testName={store.name || '...'}
            fileName={`${(store?.name || '').split(' ').join('_')}.pdf`}
            homepageUrl={getLandingUrlFromPath(getHomePageLink())}
          >
            <ViewSubjectHeaderWithTestAction
              seoData={seoData}
              seoPageData={pageData?.pageDetail?.pages}
            />
            <ViewTestSubjectPdfLayout seoData={seoData} />
          </DownloadPDF>
        </PDFSourceContext.Provider>

        {Boolean(extraInfo) && (
          <>
            <Separator pt="40px" pb="24px" variant="three-dot-horizontal" />
            <Box mb="24px">
              <ExtraInfoView extraInfo={extraInfo} fontInter />
            </Box>
          </>
        )}

        <Separator pt="40px" pb="24px" variant="three-dot-horizontal" />

        <AuthorInfo
          pageId={seoData.id}
          author={dolDinhLucAuthor}
          publishedDate={data?.lastModifiedAt ? data.lastModifiedAt : data.createdAt}
          seoUrl={seoData.url}
          withBorder
          withActions
        />
        <Box mb={32}>
          <FooterBanner />
        </Box>
      </LandingLayoutWithSEOHeader>
    );
  })
);
